/**
 * Coupon hand-off to the /coupon-codes/ page.
 *
 * This is the one piece of home-page behaviour that is NOT ported from .in:
 * .in reveals the code in place by calling its own /coupon/... endpoint. The
 * International site never does that. It navigates to /coupon-codes/<id>/,
 * and THAT page performs POST /coupons/click and POST /coupons/fresh-code.
 *
 * Names and formats are taken from the Next.js src/utils/couponCid.ts so both
 * front ends hand off identically — the destination page reads these.
 */
const CID_KEY = 'coupon_cid';
const SESSION_KEY = 'api_session_id';

/** Stable per-tab id, sent as `sessionId` by the activation calls. */
export function getOrCreateSessionId() {
  try {
    let sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : String(Date.now()) + Math.random().toString(16).slice(2);
      sessionStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  } catch {
    return '';
  }
}

/**
 * Record which listing entry a coupon was opened from, as "<couponId>|<cid>".
 * Written to BOTH sessionStorage and a cookie, same as the Next.js helper:
 * the cookie is what survives the new-tab navigation.
 */
export function setCouponCidCookie(couponId, cid) {
  if (!couponId || !cid || couponId === cid) return;
  const value = `${couponId}|${cid}`;
  try {
    sessionStorage.setItem(CID_KEY, value);
  } catch {
    /* sessionStorage can be unavailable (private mode, embedded webviews) */
  }
  document.cookie = `${CID_KEY}=${encodeURIComponent(value)}; path=/; samesite=lax`;
}

/** A gateway coupon id is a six-character alphanumeric token. */
export function isCouponCodeLink(link) {
  return typeof link === 'string' && /^[a-z0-9]{6}$/i.test(link.trim());
}

/** Path of the coupon-reveal page for an id. `activate` opens it revealed. */
export function couponPath(couponId, activate) {
  if (!couponId) return null;
  const path = `/coupon-codes/${String(couponId).toLowerCase()}/`;
  return activate ? `${path}?activate=1` : path;
}

/**
 * The redeem interstitial — the tab that goes to the merchant.
 *
 * `/coupon-codes/<id>/?activate=1` still does the same job and is what
 * grabon.com uses, so a shared or bookmarked activation link keeps working.
 * This is the route we send fresh clicks to, matching .in: it is one small
 * document instead of the whole reveal page rendered and discarded.
 */
export function redeemPath(couponId) {
  if (!couponId) return null;
  return `/redeem/${String(couponId).toLowerCase()}/`;
}
